'use client';

import React, { useState } from 'react';
import Link from 'next/link';

import SiteNav from '@/components/SiteNav';
import LandingFooter from '@/app/landing-page/components/LandingFooter';
import { trackBlogSignupSubmitted } from '@/lib/gaEvents';

// ── Inline email signup block ──────────────────────────────────────────────
function BlogSignupBlock({ articleSlug, articleTitle }: { articleSlug: string; articleTitle: string }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const res = await fetch('/api/blog-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (res.ok) {
        setStatus('success');
        setEmail('');
        trackBlogSignupSubmitted({ article_slug: articleSlug, article_title: articleTitle });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  }

  return (
    <div
      className="rounded-xl"
      style={{
        backgroundColor: '#F3F0EC',
        border: '1px solid #E5E0D8',
        borderRadius: '12px',
        padding: '32px 36px',
        margin: '40px 0',
      }}
    >
      <p
        className="font-body text-xs font-semibold uppercase tracking-widest mb-3"
        style={{ color: '#D4891A', letterSpacing: '0.14em' }}
      >
        ANNA&rsquo;S NOTES
      </p>
      <p
        className="font-display font-semibold mb-2"
        style={{ fontSize: 'clamp(1.1rem, 2vw, 1.35rem)', color: '#1B2A4A', lineHeight: '1.3' }}
      >
        Get Anna&rsquo;s occasional notes — straight to your inbox.
      </p>
      <p
        className="font-body text-sm mb-6"
        style={{ color: '#6B7280', lineHeight: '1.6' }}
      >
        Plain English. No noise. Unsubscribe any time.
      </p>

      {status === 'success' ? (
        <p className="font-body text-sm font-medium" style={{ color: '#1B2A4A' }}>
          You&rsquo;re in. Anna will be in touch.
        </p>
      ) : (
        <>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              disabled={status === 'loading'}
              className="flex-1 font-body text-sm px-4 py-3 rounded-xl outline-none transition-all duration-150"
              style={{
                border: '1px solid #E5E0D8',
                backgroundColor: '#ffffff',
                color: '#374151',
              }}
            />
            <button
              type="submit"
              disabled={status === 'loading' || !email.trim()}
              className="font-body font-semibold text-sm px-6 py-3 rounded-xl transition-all duration-200 active:scale-95 whitespace-nowrap disabled:opacity-60 sm:w-auto w-full"
              style={{ backgroundColor: '#D4891A', color: '#ffffff' }}
            >
              {status === 'loading' ? 'Sending…' : 'Send me Anna\u2019s notes \u2192'}
            </button>
          </form>
          {status === 'error' && (
            <p className="font-body text-xs mb-3" style={{ color: '#c0392b' }}>
              Something went wrong. Try again or email{' '}
              <a href="mailto:hello@thatclicked.com" style={{ color: '#c0392b', textDecoration: 'underline' }}>
                hello@thatclicked.com
              </a>{' '}
              directly.
            </p>
          )}
          <p className="font-body text-xs" style={{ color: '#9CA3AF', lineHeight: '1.6' }}>
            No spam. No weekly newsletters. Just Anna&rsquo;s notes when something worth sharing comes up.
          </p>
        </>
      )}
    </div>
  );
}
// ──────────────────────────────────────────────────────────────────────────

type ContentLevel =
  | 'L1 Cornerstone' |'L2 Questions' |'L3 Practical' |'L4 Definitions' |'L5 Anna Notes' |'L6 Ask Anna';

type SchemaType = 'Article' | 'FAQPage' | 'HowTo' | 'DefinedTerm';

interface RelatedLink {
  title: string;
  href: string;
  type?: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface BlogPostProps {
  title: string;
  slug: string;
  subheading: string;
  date: string;
  level: ContentLevel;
  schemaType: SchemaType;
  metaDescription: string;
  children: React.ReactNode;
  relatedLinks?: RelatedLink[];
  shortAnswer?: string;
  faqItems?: FaqItem[];
}

const levelBadgeStyles: Record<ContentLevel, { bg: string; text: string; label: string }> = {
  'L1 Cornerstone': { bg: 'rgba(10,36,99,0.08)', text: 'var(--navy)', label: 'L1 · Cornerstone' },
  'L2 Questions':   { bg: 'rgba(10,36,99,0.06)', text: 'var(--navy)', label: 'L2 · Questions' },
  'L3 Practical':   { bg: 'rgba(212,160,23,0.12)', text: 'var(--amber)', label: 'L3 · Practical' },
  'L4 Definitions': { bg: 'rgba(10,36,99,0.06)', text: 'var(--navy)', label: 'L4 · Definitions' },
  'L5 Anna Notes':  { bg: 'rgba(212,160,23,0.12)', text: 'var(--amber)', label: 'L5 · Anna Notes' },
  'L6 Ask Anna':    { bg: 'rgba(212,160,23,0.16)', text: 'var(--amber)', label: 'L6 · Ask Anna' },
};

const AUTHOR_ID = 'https://thatclicked.com/#anna-rippon';
const ORG_ID = 'https://thatclicked.com/#organization';
const BASE_URL = 'https://thatclicked.com';

function buildJsonLd(
  schemaType: SchemaType,
  title: string,
  slug: string,
  metaDescription: string,
  faqItems?: FaqItem[]
): Record<string, unknown> {
  const postUrl = `${BASE_URL}/blog/${slug}`;

  const breadcrumb = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: title, item: postUrl },
    ],
  };

  const personEntity = {
    '@type': 'Person',
    '@id': AUTHOR_ID,
    name: 'Anna Rippon',
    url: `${BASE_URL}/about`,
    jobTitle: 'Editorial Guide',
    worksFor: { '@type': 'Organization', '@id': ORG_ID },
    description:
      'Anna Rippon is the editorial voice behind ThatClicked, a platform helping professionals over 50 build confidence with AI tools.',
  };

  const authorRef = { '@id': AUTHOR_ID };

  const faqSchema = faqItems && faqItems.length > 0
    ? {
        '@type': 'FAQPage',
        author: authorRef,
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      }
    : null;

  let mainSchema: Record<string, unknown>;

  switch (schemaType) {
    case 'FAQPage':
      mainSchema = {
        '@type': 'FAQPage',
        name: title,
        description: metaDescription,
        author: authorRef,
        mainEntity: faqItems && faqItems.length > 0
          ? faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: { '@type': 'Answer', text: item.answer },
            }))
          : [],
      };
      break;
    case 'HowTo':
      mainSchema = {
        '@type': 'HowTo',
        name: title,
        description: metaDescription,
        author: authorRef,
      };
      break;
    case 'DefinedTerm':
      mainSchema = {
        '@type': 'DefinedTerm',
        name: title,
        description: metaDescription,
        author: authorRef,
      };
      break;
    case 'Article':
    default:
      mainSchema = {
        '@type': 'Article',
        headline: title,
        description: metaDescription,
        author: authorRef,
      };
      break;
  }

  const graph: Record<string, unknown>[] = [personEntity, breadcrumb, mainSchema];

  // Add separate FAQPage block when schemaType is not already FAQPage but faqItems provided
  if (faqSchema && schemaType !== 'FAQPage') {
    graph.push(faqSchema);
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

export default function BlogPost({
  title,
  slug,
  subheading,
  date,
  level,
  schemaType,
  metaDescription,
  children,
  relatedLinks,
  shortAnswer,
  faqItems,
}: BlogPostProps) {
  const jsonLd = buildJsonLd(schemaType, title, slug, metaDescription, faqItems);
  const badge = levelBadgeStyles[level];
  const isSpeakableLevel = level === 'L2 Questions' || level === 'L4 Definitions';

  const speakableJsonLd = isSpeakableLevel && shortAnswer
    ? {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['#short-answer'],
        },
      }
    : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      {speakableJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(speakableJsonLd) }}
        />
      )}

      <SiteNav variant="light" currentPath="/blog" />

      {/* Article */}
      <main className="pt-24 pb-24 lg:pt-36 lg:pb-36">
        <div className="max-w-2xl mx-auto px-6 lg:px-0">

          {/* Visible breadcrumb trail */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-1 flex-wrap" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li>
                <Link
                  href="/"
                  className="font-body transition-opacity duration-150 hover:opacity-70"
                  style={{ fontSize: '0.75rem', color: '#AAAAAA', textDecoration: 'none' }}
                >
                  Home
                </Link>
              </li>
              <li style={{ fontSize: '0.75rem', color: '#CCCCCC', margin: '0 2px' }} aria-hidden="true">›</li>
              <li>
                <Link
                  href="/blog"
                  className="font-body transition-opacity duration-150 hover:opacity-70"
                  style={{ fontSize: '0.75rem', color: '#AAAAAA', textDecoration: 'none' }}
                >
                  Blog
                </Link>
              </li>
              <li style={{ fontSize: '0.75rem', color: '#CCCCCC', margin: '0 2px' }} aria-hidden="true">›</li>
              <li>
                <span
                  className="font-body"
                  style={{ fontSize: '0.75rem', color: '#AAAAAA' }}
                  aria-current="page"
                >
                  {title}
                </span>
              </li>
            </ol>
          </nav>

          {/* Eyebrow */}
          <div className="flex items-center gap-2 mb-6">
            <div className="h-px w-8" style={{ backgroundColor: 'var(--amber)' }} aria-hidden="true" />
            <span
              className="font-body text-xs font-600 uppercase tracking-widest"
              style={{ color: 'var(--amber)', letterSpacing: '0.12em' }}
            >
              That Clicked Blog
            </span>
          </div>

          {/* Content level badge */}
          <div className="mb-4">
            <span
              className="inline-block font-body text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1"
              style={{
                backgroundColor: badge.bg,
                color: badge.text,
                letterSpacing: '0.1em',
              }}
            >
              {badge.label}
            </span>
          </div>

          {/* Title */}
          <h1
            className="font-display font-semibold mb-4"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              color: 'var(--navy)',
              lineHeight: '1.18',
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </h1>

          {/* Subheading */}
          {subheading && (
            <p
              className="font-body text-lg mb-5 leading-relaxed"
              style={{ color: 'var(--text-light)', lineHeight: '1.6' }}
            >
              {subheading}
            </p>
          )}

          {/* Byline */}
          <p className="font-body text-sm mb-2" style={{ color: 'var(--text-light)' }}>
            By{' '}
            <Link
              href="/about"
              className="underline underline-offset-2 hover:opacity-70 transition-opacity duration-150"
              style={{ color: 'var(--text-light)' }}
            >
              Anna Rippon
            </Link>
            , creator of ThatClicked
          </p>

          {/* Date — demoted below byline, small and light grey */}
          <p
            className="font-body mb-12"
            style={{ color: '#AAAAAA', fontSize: '0.75rem' }}
          >
            {date}
          </p>

          {/* Short Answer block — only renders when shortAnswer prop is provided */}
          {shortAnswer && (
            <div
              id="short-answer"
              style={{
                backgroundColor: '#FFFDF5',
                borderLeft: '3px solid #D4891A',
                borderRadius: '0 3px 3px 0',
                padding: '18px 22px',
                margin: '0 0 32px 0',
              }}
            >
              <p
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '11px',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: '#D4891A',
                  marginBottom: '8px',
                }}
              >
                The Short Answer
              </p>
              <p
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '15px',
                  lineHeight: '1.75',
                  color: '#1B2A4A',
                  fontWeight: 'normal',
                  fontStyle: 'normal',
                }}
              >
                {shortAnswer}
              </p>
            </div>
          )}

          {/* Divider */}
          <div className="h-px w-full mb-10" style={{ backgroundColor: 'var(--border)' }} aria-hidden="true" />

          {/* Article body */}
          <article className="prose-anna space-y-6" style={{ maxWidth: '660px', lineHeight: '1.85' }}>
            {children}
          </article>

          {/* Related links block — only renders when relatedLinks provided and non-empty */}
          {relatedLinks && relatedLinks.length > 0 && (
            <>
              <div className="h-px w-full mt-14 mb-10" style={{ backgroundColor: 'var(--border)' }} aria-hidden="true" />
              <div>
                <p
                  className="font-body text-xs font-semibold uppercase tracking-widest mb-6"
                  style={{ color: 'var(--text-light)', letterSpacing: '0.12em' }}
                >
                  You may also want
                </p>
                <div className="flex flex-col gap-5">
                  {relatedLinks.map((link) => (
                    <div key={link.href}>
                      <Link
                        href={link.href}
                        className="group inline-flex items-start gap-2 transition-opacity duration-150 hover:opacity-70"
                      >
                        <span style={{ color: 'var(--amber)', flexShrink: 0, marginTop: '1px' }}>→</span>
                        <span
                          className="font-body text-sm font-medium"
                          style={{ color: 'var(--navy)', lineHeight: '1.5' }}
                        >
                          {link.title}
                        </span>
                      </Link>
                      {link.type && (
                        <p
                          className="font-body text-xs mt-1 ml-6"
                          style={{ color: '#AAAAAA' }}
                        >
                          {link.type}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Divider before CTA */}
          <div className="h-px w-full mt-14 mb-12" style={{ backgroundColor: 'var(--border)' }} aria-hidden="true" />

          {/* Email signup block */}
          <BlogSignupBlock articleSlug={slug} articleTitle={title} />

          {/* TODO: Course CTA */}

        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
